/**
 * AudioPlayer — skalowalny moduł odtwarzacza audio
 * ------------------------------------------------------
 * Obsługuje wiele instancji odtwarzaczy na stronie. 
 * Każda instancja działa niezależnie, z własnym przyciskiem i paskiem postępu.
 *
 * Struktura HTML:
 * <div class="playeros">
 *   <canvas class="player-circle" width="170" height="170"></canvas>  // pasek postępu
 *   <div class="btn-palyer trigger-player" data-audio="sound.mp3">
 *     <div class="btn-palyer-fog">
 *       <svg>...</svg>  // ikona play
 *     </div>
 *   </div>
 * </div>
 *
 * Atrybuty danych (data-*):
 * - data-audio → ścieżka do pliku audio dla przycisku
 *
 * Zachowanie:
 * - Kliknięcie przycisku odtwarza lub pauzuje odpowiadający plik audio
 * - Każdy przycisk posiada klasę 'played' podczas odtwarzania
 * - Pasek postępu (.player-circle) aktualizuje się w czasie rzeczywistym
 * - IntersectionObserver zatrzymuje odtwarzanie, gdy kontener nie jest widocz*
 **/


export class AudioPlayer {
  constructor(container) {
    this.container = container;

    this.audio =
      container.querySelector('audio') || document.createElement('audio');
    if (!container.contains(this.audio)) container.appendChild(this.audio);

    this.buttons = container.querySelectorAll('.trigger-player');
    this.circleCanvases = container.querySelectorAll('.player-circle');

    this.currentButton = null;
    this.animFrame = null;

    // flagi do observera
    this.userPaused = false;
    this.pausedByObserver = false;

    this.initButtons();
    this.initAudioEvents();
    this.initVisibilityObserver();

    // stan początkowy
    this.container.classList.remove('playing');
    this.circleCanvases.forEach(c => this.drawCircle(c, 0));
  }

  /* ------------------------------------ */
  /* Buttons                              */
  /* ------------------------------------ */

  initButtons() {
    this.buttons.forEach(button => {
      button.addEventListener('click', async () => {
        const sameButton = this.currentButton === button;
        const isPlaying = !this.audio.paused;

        // klik = intencja użytkownika
        this.container.classList.add('playing');
        this.userPaused = false;

        if (sameButton) {
          if (isPlaying) {
            this.audio.pause();
            this.userPaused = true;
            button.classList.remove('played');
          } else {
            await this.audio.play().catch(() => {});
            button.classList.add('played');
          }
          return;
        }

        if (this.currentButton) {
          this.currentButton.classList.remove('played');
        }

        this.currentButton = button;
        button.classList.add('played');

        if (this.audio.src !== button.dataset.audio) {
          this.audio.src = button.dataset.audio;
          await new Promise(resolve =>
            this.audio.addEventListener('canplay', resolve, { once: true })
          );
        }

        await this.audio.play().catch(() => {});
      });
    });
  }

  /* ------------------------------------ */
  /* Audio events                         */
  /* ------------------------------------ */
  initAudioEvents() {
    this.audio.addEventListener('play', () => {
      this.updateCircles();
    });

    this.audio.addEventListener('pause', () => {
      cancelAnimationFrame(this.animFrame);
    });

    this.audio.addEventListener('ended', () => {
      cancelAnimationFrame(this.animFrame);

      if (this.currentButton) {
        this.currentButton.classList.remove('played');
        this.currentButton = null;
      }

      this.userPaused = false;
      this.pausedByObserver = false;

      this.container.classList.remove('playing');
      this.circleCanvases.forEach(c => this.drawCircle(c, 0));
    });
  }

  /* ------------------------------------ */
  /* IntersectionObserver                 */
  /* ------------------------------------ */
  initVisibilityObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isActive = this.container.classList.contains('playing');

        // wyjechaliśmy z widoku
        if (!entry.isIntersecting && isActive && !this.audio.paused) {
          this.pausedByObserver = true;
          this.audio.pause();
        }

        // wróciliśmy — wznów TYLKO jeśli observer pauzował
        if (
          entry.isIntersecting &&
          this.pausedByObserver &&
          !this.userPaused
        ) {
          this.pausedByObserver = false;
          this.audio.play().catch(() => {});
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(this.container);
  }

  /* ------------------------------------ */
  /* Canvas                               */
  /* ------------------------------------ */
  drawCircle(canvas, progress) {
    const ctx = canvas.getContext('2d');
    const r = canvas.width / 2 - 5;
    const c = canvas.width / 2;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.beginPath();
    ctx.arc(c, c, r, 0, 2 * Math.PI);
    ctx.strokeStyle = '#f1f1f1';
    ctx.lineWidth = 10;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(
      c,
      c,
      r,
      -Math.PI / 2,
      -Math.PI / 2 + 2 * Math.PI * progress
    );
    ctx.strokeStyle = '#f00';
    ctx.lineWidth = 10;
    ctx.stroke();
  }

  updateCircles() {
    const progress =
      this.audio.duration > 0
        ? this.audio.currentTime / this.audio.duration
        : 0;

    this.circleCanvases.forEach(c =>
      this.drawCircle(c, progress)
    );

    if (!this.audio.paused) {
      this.animFrame = requestAnimationFrame(() => this.updateCircles());
    }
  }
}
